/**
 * Client Configuration Loader
 * Loads public configuration with build-time defaults and runtime config.json override
 */

import type { AppPublicConfig, Environment } from "../../shared/config/types";
import { getDefaultAppPublicConfig } from "../../shared/config/defaults";
import { validateAppPublicConfig } from "../../shared/config/validate";
import { deepMerge } from "../../shared/config/merge";

// Cache for resolved config
let cachedConfig: AppPublicConfig | null = null;

/**
 * Detect environment from available sources (deterministic order)
 */
function detectEnvironment(): Environment {
  // 1. Check globalThis.__OPTLAB_ENV__ (runtime global, if set via bootstrap)
  if (typeof globalThis !== "undefined" && (globalThis as any).__OPTLAB_ENV__) {
    const env = (globalThis as any).__OPTLAB_ENV__;
    if (env === "dev" || env === "stage" || env === "prod") {
      return env;
    }
  }

  // 2. Check __OPTLAB_ENV__ (compile-time constant from DefinePlugin)
  if (typeof __OPTLAB_ENV__ !== "undefined") {
    return __OPTLAB_ENV__;
  }

  // 3. Fallback to process.env.OPTLAB_ENV (only if bundler injects it)
  // @ts-ignore - process.env may be injected by bundler
  if (typeof process !== "undefined" && process.env && process.env.OPTLAB_ENV) {
    // @ts-ignore
    const env = process.env.OPTLAB_ENV;
    if (env === "dev" || env === "stage" || env === "prod") {
      return env;
    }
  }

  // 4. Default to "dev"
  return "dev";
}

/**
 * Fetch runtime config.json if available
 * Returns null on any error (404, network, parse, etc.)
 * Fails open: always returns null on error, never throws
 */
async function fetchRuntimeConfig(): Promise<Partial<AppPublicConfig> | null> {
  try {
    const configUrl = new URL("config.json", typeof window !== "undefined" ? window.location.origin : "http://localhost").toString();
    const response = await fetch(configUrl, { cache: "no-store" });

    // 404 means no override file exists - not an error
    if (response.status === 404) {
      return null;
    }

    // Any other non-OK status - treat as error, fall back to defaults
    if (!response.ok) {
      // Optional dev log (only in dev environment)
      if (typeof __OPTLAB_ENV__ !== "undefined" && __OPTLAB_ENV__ === "dev") {
        console.log("[Config] runtime config fetch failed status=" + response.status);
      }
      return null;
    }

    // Check Content-Type to ensure we're getting JSON, not HTML (e.g., 404 page)
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      // Optional dev log
      if (typeof __OPTLAB_ENV__ !== "undefined" && __OPTLAB_ENV__ === "dev") {
        console.log("[Config] invalid config content-type: " + contentType);
      }
      return null;
    }

    // Try to parse JSON
    try {
      const json = await response.json();
      return json as Partial<AppPublicConfig>;
    } catch (parseError) {
      // JSON parse failed - log and return null
      if (typeof __OPTLAB_ENV__ !== "undefined" && __OPTLAB_ENV__ === "dev") {
        const errorMsg = parseError instanceof Error ? parseError.message : String(parseError);
        console.log("[Config] runtime config JSON parse error: " + errorMsg);
      }
      return null;
    }
  } catch (error) {
    // Network or any other error - silently fail and continue with defaults
    // Optional dev log
    if (typeof __OPTLAB_ENV__ !== "undefined" && __OPTLAB_ENV__ === "dev") {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.log("[Config] runtime config fetch error: " + errorMsg);
    }
    return null;
  }
}

/**
 * Get public configuration (async, fetches config.json)
 * Merge precedence: defaults < runtime config.json
 * Always returns defaults if runtime config is invalid or unavailable
 */
export async function getPublicConfig(): Promise<AppPublicConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  const env = detectEnvironment();
  const defaults = getDefaultAppPublicConfig(env);

  // Attempt to fetch and merge runtime config (with error handling)
  let finalConfig: AppPublicConfig = defaults;

  try {
    const runtimeConfig = await fetchRuntimeConfig();

    if (runtimeConfig) {
      try {
        // Merge runtime config over defaults
        let merged = deepMerge(defaults, runtimeConfig);
        
        // Enforce environment-specific rules: diagnostics can never be enabled in prod
        // Even if config.json tries to enable it, we override it
        // (merged is frozen, so we need to create a new object)
        if (merged.env !== "dev" && merged.public.featureFlags.diagnostics === true) {
          merged = {
            ...merged,
            public: {
              ...merged.public,
              featureFlags: {
                ...merged.public.featureFlags,
                diagnostics: false,
              },
            },
          };
        }
        
        // Validate merged config - if invalid, this will throw
        finalConfig = validateAppPublicConfig(merged);
      } catch (validationError) {
        // Validation failed - runtime config is invalid
        // Fall back to defaults, optionally log in dev
        if (typeof __OPTLAB_ENV__ !== "undefined" && __OPTLAB_ENV__ === "dev") {
          const errorMsg = validationError instanceof Error ? validationError.message : String(validationError);
          console.log("[Config] runtime config validation failed, using defaults: " + errorMsg);
        }
        // finalConfig already set to defaults above
      }
    }
  } catch (error) {
    // Any unexpected error in fetch/merge process - fall back to defaults
    // Optional dev log
    if (typeof __OPTLAB_ENV__ !== "undefined" && __OPTLAB_ENV__ === "dev") {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.log("[Config] runtime config processing error, using defaults: " + errorMsg);
    }
    // finalConfig already set to defaults above
  }

  // Cache and return (always defaults or valid merged config)
  cachedConfig = finalConfig;
  return finalConfig;
}

/**
 * Get public configuration synchronously (unsafe, defaults only)
 * Use only in contexts where async is impossible
 */
export function getPublicConfigSyncUnsafe(): AppPublicConfig {
  const env = detectEnvironment();
  return getDefaultAppPublicConfig(env);
}
